import { ZAttribute } from "src/types/ZAttribute";
import { ZUid } from "src/types/zUid";
import { apiAddExistingAttr } from "../../objectsApi";
import { CatalogStore } from "../model/catalog";
import { ExpansionStore } from "../model/expansion";
import {
  GroupData,
  NodeId,
  ObjectNodeData,
  attributeNode,
  attributeNodeId,
  groupNode,
  objectNodeId,
  unsavedId,
} from "../model/node";
import { SelectionStore } from "../model/selection";
import { TreeStore } from "../model/tree";
import { NodeLoading } from "./loading";
import { NodeSelection } from "./selection";

/**
 * Появление узлов до обращения к серверу.
 *
 * Новый объект, новая группа, новый атрибут существуют только на клиенте,
 * пока пользователь не сохранит форму. Отделено от записи: здесь нет ни одного
 * обращения к сети, кроме привязки уже существующего атрибута к значению.
 */
export class DraftNodes {
  constructor(
    private readonly tree: TreeStore,
    private readonly catalog: CatalogStore,
    private readonly selection: SelectionStore,
    private readonly expansion: ExpansionStore,
    private readonly nodeSelection: NodeSelection,
    private readonly loading: NodeLoading,
  ) {}

  /** Новый объект: существует только на клиенте, пока его не сохранят. */
  async createDraftObject() {
    const draft: ObjectNodeData = {
      id: unsavedId,
      displayId: null,
      name: "",
      canUpdate: true,
      canDelete: true,
    };
    this.catalog.upsert(draft);
    await this.nodeSelection.select(objectNodeId(unsavedId), true);
  }

  /** Новый атрибут внутри выбранного узла. */
  async addDraftAttribute() {
    await this.addDraftChild({
      id: unsavedId,
      name: "",
      // Тип выбирает пользователь в форме; до этого поля значения нет
      valueType: undefined as unknown as number,
    });
  }

  /** Новая группа выбранного типа внутри выбранного узла. */
  async addDraftGroup(data: GroupData) {
    await this.addDraftChild(data);
  }

  /**
   * Отказ от несохранённой сущности: пользователь ушёл с формы, не сохранив
   * только что созданный узел.
   */
  async dropIfDraft() {
    const id = this.selection.selectedId;
    const node = id ? this.tree.node(id) : undefined;
    if (!node || node.data.id !== unsavedId) return;
    if (node.kind === "object") this.catalog.remove(unsavedId);
    else this.tree.remove(node.id);
    await this.nodeSelection.select(null);
  }

  /** Новый атрибут или группа внутри выбранного узла. */
  private async addDraftChild(data: GroupData | ZAttribute) {
    const ownerId = this.selection.selectedId;
    if (!ownerId) return;
    // Иначе черновик станет единственным ребёнком, а настоящие дети исчезнут
    await this.loading.ensureChildren(ownerId);
    const child =
      "groupType" in data
        ? groupNode(data, ownerId)
        : attributeNode(data, ownerId);
    this.tree.appendChild(ownerId, child);
    this.expansion.set(ownerId, true);
    await this.nodeSelection.select(child.id, true);
  }

  /**
   * Привязка существующего атрибута к значению справочника.
   * Описание атрибута берётся из уже загруженного поддерева группы.
   */
  async attachExistingAttr(valueNodeId: NodeId, attrId: ZUid) {
    const value = this.tree.node(valueNodeId);
    if (value?.kind !== "value") throw Error("Ожидалось значение справочника");
    const group = this.tree.node(value.ownerId);
    if (group?.kind !== "group") throw Error("Не найдена группа значения");
    const attr = this.findAttr(group.id, attrId);
    if (!attr) throw Error("Не найден атрибут по id");
    await apiAddExistingAttr(group.data.id, value.data.id, attrId);
    await this.loading.ensureChildren(valueNodeId);
    this.tree.appendChild(valueNodeId, attributeNode(attr, valueNodeId));
    this.expansion.set(valueNodeId, true);
    await this.nodeSelection.select(attributeNodeId(attrId, valueNodeId));
  }

  private findAttr(rootId: NodeId, attrId: ZUid): ZAttribute | undefined {
    const found = this.tree
      .subtreeIds(rootId)
      .map((id) => this.tree.node(id))
      .find((node) => node?.kind === "attribute" && node.data.id === attrId);
    return found?.kind === "attribute" ? found.data : undefined;
  }
}
